import {createContext, useCallback, use, useState, useMemo, type ReactNode} from 'react'
import {useSearchParams} from '@github-ui/use-navigate'
import {Period, PeriodMap} from '../types'

interface PulsePeriodContextType {
  selectedPeriod: Period
  onPeriodChange: (period: Period) => void
}

const PulsePeriodContext = createContext<PulsePeriodContextType | undefined>(undefined)

interface PulsePeriodProviderProps {
  children: ReactNode
  defaultPeriod?: Period
}

export function PulsePeriodProvider({children, defaultPeriod = Period.weekly}: PulsePeriodProviderProps) {
  const [searchParams, setSearchParams] = useSearchParams()

  if (searchParams.has('period') && Period[searchParams.get('period') as keyof typeof Period]) {
    defaultPeriod = Period[searchParams.get('period') as keyof typeof Period]
  }

  const [selectedPeriod, setSelectedPeriod] = useState(defaultPeriod)

  const handlePeriodChange = useCallback(
    (period: Period) => {
      setSelectedPeriod(period)
      setSearchParams(prev => {
        const params = new URLSearchParams(prev)
        params.set('period', PeriodMap[period])
        return params
      })
    },
    [setSearchParams, setSelectedPeriod],
  )

  const value = useMemo<PulsePeriodContextType>(
    () => ({
      selectedPeriod,
      onPeriodChange: handlePeriodChange,
    }),
    [selectedPeriod, handlePeriodChange],
  )

  return <PulsePeriodContext value={value}>{children}</PulsePeriodContext>
}

export function usePulsePeriod() {
  const context = use(PulsePeriodContext)
  if (context === undefined) {
    throw new Error('usePulsePeriod must be used within a PulsePeriodProvider')
  }
  return context
}

try{ PulsePeriodContext.displayName ||= 'PulsePeriodContext' } catch {}
try{ PulsePeriodProvider.displayName ||= 'PulsePeriodProvider' } catch {}